import { Box, Typography, Chip } from '@mui/material'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { useState } from 'react'
import dayjs from 'dayjs'
import { useRouteQuery, useTranslator } from '@/hooks'
import { BalanceRecord } from '@/@data/balance'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { ICompany } from '@/@data'
import { BalanceFilterTable } from './BalanceFilterTable'
import { useNavigate } from 'react-router-dom'
import { getInitials } from '@/utils/getInitials'

interface BalanceTableProps {
  data: BalanceRecord[]
  meta: unknown
  onRowClick?: (record: BalanceRecord) => void
  refetch?: () => void
  openStatistic: boolean
  setOpenStatistic: () => void
}

export const BalanceTable = ({ data, meta, openStatistic, setOpenStatistic }: BalanceTableProps) => {
  const { __, __n } = useTranslator()
  const { searchQuery } = useRouteQuery()
  const navigate = useNavigate()

  const getAvatar = (params: Pick<ICompany, 'logo_file' | 'name'>) => {
    const { logo_file, name } = params

    if (logo_file) {
      return <CustomAvatar src={logo_file.tiny_url} skin='light' size={34} />
    } else {
      return (
        <CustomAvatar skin='light' size={34}>
          {getInitials(name as string)}
        </CustomAvatar>
      )
    }
  }

  const monthParam = searchQuery.get('month') || dayjs().startOf('month').format('YYYY-MM-DD')

  const COLUMN_KEYS = {
    EMPLOYEE: 'employee',
    REQUESTED_TRANSACTION: 'requested_transaction',
    TOTAL_EWA: 'total_ewa_disbursement',
    EST_MAX_EWA: 'est_max_ewa_disbursement',
    EXPIRED_CONTRACT: 'expired_contract',
    SERVICES_FEE: 'services_fee',
    VOUCHER: 'voucher',
    SERVICES_TAX: 'services_tax',
    VOUCHER_TAX: 'voucher_tax'
  }

  const columnMap = [
    { key: COLUMN_KEYS.EMPLOYEE, label: __('Employee') },
    { key: COLUMN_KEYS.REQUESTED_TRANSACTION, label: __('Requested Transaction') },
    { key: COLUMN_KEYS.TOTAL_EWA, label: __('Total EWA Disbursement') },
    { key: COLUMN_KEYS.EST_MAX_EWA, label: __('Est. Max EWA Disbursement') },
    { key: COLUMN_KEYS.EXPIRED_CONTRACT, label: __('Expired Contract') },
    { key: COLUMN_KEYS.SERVICES_FEE, label: __('Services Fee') },
    { key: COLUMN_KEYS.VOUCHER, label: __('Voucher') },
    { key: COLUMN_KEYS.SERVICES_TAX, label: __('Services Tax') },
    { key: COLUMN_KEYS.VOUCHER_TAX, label: __('Voucher Tax') }
  ]

  const [visibleColumns, setVisibleColumns] = useState<string[]>(columnMap.map(c => c.key))

  const isColumnVisible = (columnKey: string) => visibleColumns.includes(columnKey)

  return (
    <>
      {/* Toolbar: limit + select columns */}
      <BalanceFilterTable
        onStatistic={setOpenStatistic}
        openStatistic={openStatistic}
        visibleColumns={visibleColumns}
        columns={columnMap}
        onSelectColumns={cols => {
          setVisibleColumns(cols)
        }}
      />

      <Box sx={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #e0e0e0'
          }}
        >
          <thead>
            <tr className='border-right-custom'>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase'
                }}
              >
                {__('Employer')}
              </th>
              {isColumnVisible(COLUMN_KEYS.EMPLOYEE) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Employee')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.REQUESTED_TRANSACTION) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Requested Transaction')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.TOTAL_EWA) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Total EWA Disbursement')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.EST_MAX_EWA) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Est. Max EWA Disbursement')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.EXPIRED_CONTRACT) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Expired Contract')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.SERVICES_FEE) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Services Fee')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.VOUCHER) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Voucher')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.SERVICES_TAX) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Services Tax')}
                </th>
              )}
              {isColumnVisible(COLUMN_KEYS.VOUCHER_TAX) && (
                <th
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    borderBottom: '1px solid #e0e0e0',
                    fontWeight: 500,
                    fontSize: '13px',
                    textTransform: 'uppercase'
                  }}
                >
                  {__('Voucher Tax')}
                </th>
              )}
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase'
                }}
              >
                {__('Action')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + 2}
                  style={{
                    padding: '40px 16px',
                    textAlign: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    color: '#666'
                  }}
                >
                  <div className='flex flex-col items-center gap-2'>
                    <Typography variant='body1' color='text.secondary'>
                      {__('No data ')}
                    </Typography>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const voucherAmount = parseFloat(row.voucher_discount)
                const serviceFee = parseFloat(row.service_fee)
                const vatRate = parseFloat(row.vat_rate)

                const servicesTax = serviceFee - Math.round(serviceFee / (1 + vatRate / 100))

                const voucherTax = voucherAmount - Math.round(voucherAmount / (1 + vatRate / 100))

                return (
                  <tr key={index}>
                    <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                      <div
                        onClick={() => navigate(`/cms/finance/balance/${row?.company_id}?month=${monthParam}`)}
                        className='flex items-center gap-4 no-underline cursor-pointer'
                      >
                        {getAvatar({ logo_file: row.company?.logo_file, name: row.company?.name || '-' })}
                        <div className='flex flex-col'>
                          <Typography
                            className='font-medium max-w-[200px] whitespace-nowrap overflow-hidden text-ellipsis text-bold'
                            color='text.primary'
                          >
                            {row.company?.name || '-'}
                          </Typography>
                          <Typography variant='body2'>{row.company?.system_code || '-'}</Typography>
                        </div>
                      </div>
                    </td>
                    {isColumnVisible(COLUMN_KEYS.EMPLOYEE) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <div className='flex items-center gap-2'>
                          <i className='tabler-users text-[#3551F2]' />
                          <Typography className='text-[15px] leading-[22px] font-normal'>
                            {row?.company?.total_user || '-'}
                          </Typography>
                        </div>
                      </td>
                    )}
                    {isColumnVisible(COLUMN_KEYS.REQUESTED_TRANSACTION) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <div className='flex items-center gap-2'>
                          <i className='tabler-clipboard-list text-[#FF9F43]' />
                          <Typography className='text-[15px] leading-[22px] font-normal'>
                            {__n(row.request_num)}
                          </Typography>
                        </div>
                      </td>
                    )}
                    {isColumnVisible(COLUMN_KEYS.TOTAL_EWA) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {__n(row.request_amount || 0, 'đ')}
                        </Typography>
                      </td>
                    )}
                    {isColumnVisible(COLUMN_KEYS.EST_MAX_EWA) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {__n(row.eligibility_amount || 0, 'đ')}
                        </Typography>
                      </td>
                    )}
                    {isColumnVisible(COLUMN_KEYS.EXPIRED_CONTRACT) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {row.contract ? dayjs(row.contract?.end_date).format('DD/MM/YYYY') : '-'}
                        </Typography>
                      </td>
                    )}
                    {isColumnVisible(COLUMN_KEYS.SERVICES_FEE) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {__n(row.service_fee || 0, 'đ')}
                        </Typography>
                      </td>
                    )}
                    {isColumnVisible(COLUMN_KEYS.VOUCHER) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Box className='flex items-center gap-2'>
                          {voucherAmount > 0 && (
                            <Chip
                              label={`${__n(voucherAmount)} đ`}
                              size='small'
                              sx={{
                                backgroundColor: '#E8E6FC',
                                color: '#3551F2',
                                borderRadius: '12px',
                                fontSize: '12px'
                              }}
                            />
                          )}
                          <Typography className='text-[15px] leading-[22px] font-normal'>
                            {__n(voucherAmount || 0, 'đ')}
                          </Typography>
                        </Box>
                      </td>
                    )}
                    {isColumnVisible(COLUMN_KEYS.SERVICES_TAX) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {__n(servicesTax || 0, 'đ')}
                        </Typography>
                      </td>
                    )}
                    {isColumnVisible(COLUMN_KEYS.VOUCHER_TAX) && (
                      <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                        <Typography className='text-[15px] leading-[22px] font-normal'>
                          {__n(voucherTax || 0, 'đ')}
                        </Typography>
                      </td>
                    )}
                    <td style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                      <div
                        onClick={() => navigate(`/cms/finance/balance/${row?.company_id}?month=${monthParam}`)}
                        className='flex items-center gap-4'
                      >
                        <i className='tabler-external-link text-[#2F2B3DB2] text-[18px]' />
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </Box>

      {meta && (
        <TablePagination
          meta={
            {
              ...(meta as any),
              page: Number(searchQuery.get('page')) || 1
            } as unknown as TablePaginationProps
          }
        />
      )}
    </>
  )
}
